<!--
	Draws the Clear Zone cross-section profile as an inline SVG from the feature's
	`cross_section` z-value series. Rendered by AttributesPanel for the Clear Zone layer.
-->
<script lang="ts">
	import { convert } from '#lib/intlUnits.js';
	import { unitSystemStore } from '#lib/stores/unitSystemStore.svelte.js';

	interface Props {
		clearzone_grade: string;
		cross_section: string;
		clearzone_start_offset_m: number;
		backslope_distance_m: number;
		foreslope_distance_m: number;
	}

	const {
		clearzone_grade,
		cross_section,
		clearzone_start_offset_m,
		backslope_distance_m,
		foreslope_distance_m
	}: Props = $props();

	// profile is relative to the clear zone test start point; points are a fixed distance apart along the profile.
	const csArray = $derived(cross_section.split(',').map((x: string) => parseFloat(x)));

	// actual render dimensions are controlled by CSS below
	// Internal scaling (i.e. if value of 10 then 1px SVG = 0.1 meter)
	const internalScaling = 16;

	// where the clear zone test starts/ends
	const testStart = $derived(clearzone_start_offset_m * internalScaling);

	const backTestEnd = $derived((clearzone_start_offset_m + backslope_distance_m) * internalScaling);
	const foreTestEnd = $derived((clearzone_start_offset_m + foreslope_distance_m) * internalScaling);

	// profile points are sampled every 0.5m, so points/2 = meters width
	const width_m = $derived((csArray.length - 1) / 2);
	const width = $derived(internalScaling * width_m);
	// we show +/- this rise/fall from baseline 0 (i.e. the road)
	const height_m = 8;
	const height = height_m * internalScaling;

	const metric = $derived(unitSystemStore.systemFor('length') === 'metric');
	// internal coords are metres; grid step is 1 m (metric) or 5 ft (imperial)
	const unitScale = $derived(metric ? 1 : convert(5, 'length', 'imperial', 'metric'));
	const scaleBarPosition = $derived(metric ? 10 : 7);
	// Horizontal grid lines every unit
	const gridTicks = $derived(
		Array.from(
			{ length: height / (internalScaling * unitScale) },
			(_, i) => (i + 1) * (internalScaling * unitScale)
		)
	);

	// Vertical grid lines every meter
	const gridLines = $derived(
		Array.from(
			{ length: width / (internalScaling * unitScale) },
			(_, i) => (i + 1) * (internalScaling * unitScale)
		)
	);

	const linePoints = $derived(
		csArray.map((z, i) => `${(internalScaling * i) / 2},${-internalScaling * z}`).join(' ')
	);
	// Close the area down to the plot floor for the fill.
	const areaPoints = $derived(`${linePoints} ${width},${height * 2} ${0},${height * 2}`);

	const polyF = $derived(
		`${testStart},${height} ${width},${height} ${width},${-height} ${testStart},${-height}`
	);
	const getGradeAreaPoly = (xOffset: number, width: number, grade: number) => {
		const endWidth = width + xOffset;
		return `${xOffset},0 ${endWidth},${(1 / grade) * width} ${endWidth},${(-1 / grade) * width}`;
	};

	// Fixed plot palette: the pastel grade fills don't theme, so the ink over them stays dark too.
	const PLOT = { background: '#ffffff', ink: '#363838', profile: '#5f6163' };
	// Drawn F to A so each flatter grade layers on top; slope is the 1:n boundary, labelY in metres.
	const GRADES = [
		{ grade: 'F', slope: 0, fill: '#e6928e', labelY: -5.7 },
		{ grade: 'E', slope: 2, fill: '#f8c1a7', labelY: -4.0 },
		{ grade: 'D', slope: 3, fill: '#f9eac0', labelY: -2.7 },
		{ grade: 'C', slope: 4, fill: '#e7f2c0', labelY: -1.9 },
		{ grade: 'B', slope: 6, fill: '#c3e2aa', labelY: -1.0 },
		{ grade: 'A', slope: 12, fill: '#87c6a2', labelY: -0.14 }
	];
</script>

<div class="container">
	<h3 class="heading">Profile</h3>
	{#if clearzone_grade === 'Unknown'}
		<div class="clearZoneUnknown">Clear zone could not be assessed at this location.</div>
	{:else}
		<div class="imageContainer">
			<svg
				viewBox={`0 ${-1 * height} ${width} ${height * 2}`}
				role="img"
				aria-label="Clear zone cross-section, grade {clearzone_grade}"
				style:--plot-background={PLOT.background}
				style:--plot-ink={PLOT.ink}
				style:--plot-profile={PLOT.profile}>
				<rect class="plotBackground" x={0} y={-height} {width} height={height * 2} />
				{#each GRADES as { grade, slope, fill } (grade)}
					<polygon
						class:gradeArea={slope > 0}
						{fill}
						points={slope ? getGradeAreaPoly(testStart, width, slope) : polyF} />
				{/each}
				<!-- grid marks -->
				{#each gridTicks as t (t)}
					<line class="gridLine" x1={0} x2={width} y1={t} y2={t} />
					<line class="gridLine" x1={0} x2={width} y1={-t} y2={-t} />
				{/each}
				{#each gridLines as t (t)}
					<line class="gridLine" x1={t} x2={t} y1={-height} y2={height} />
				{/each}
				<!-- road level line -->
				<line class="baseline" x1={0} x2={width} y1={0} y2={0} />
				<!-- Profile -->
				<polyline class="profileLine" points={linePoints} />
				<polygon class="profileArea" points={areaPoints} />
				<!-- Clear zone test start/end -->
				<line class="czTestLine" x1={testStart} x2={testStart} y1={-height} y2={height} />
				<line class="czTestLine" x1={backTestEnd} x2={backTestEnd} y1={-height} y2={0} />
				<line class="czTestLine" x1={foreTestEnd} x2={foreTestEnd} y1={0} y2={height} />
				{#each GRADES as { grade, labelY } (grade)}
					<text class="gradeText" x={testStart + 10 * internalScaling} y={labelY * internalScaling}
						>{grade}</text>
				{/each}
				<!-- Indicator to CZ test start/end -->
				<line
					class="explainLine"
					x1={testStart}
					x2={backTestEnd}
					y1={-height + 1.7 * internalScaling}
					y2={-height + 1.7 * internalScaling}></line>
				<polygon
					class="explainArrow"
					points={`${testStart},${-height + 1.7 * internalScaling} ${testStart + 0.5 * internalScaling},${-height + 1.7 * internalScaling + 0.2 * internalScaling} ${testStart + 0.5 * internalScaling},${-height + 1.7 * internalScaling - 0.2 * internalScaling}`} />
				<polygon
					class="explainArrow"
					points={`${backTestEnd},${-height + 1.7 * internalScaling} ${backTestEnd - 0.5 * internalScaling},${-height + 1.7 * internalScaling + 0.2 * internalScaling} ${backTestEnd - 0.5 * internalScaling},${-height + 1.7 * internalScaling - 0.2 * internalScaling}`} />
				<text
					class="explainText"
					text-anchor="middle"
					x={testStart + (backTestEnd - testStart) / 2}
					y={-height + 1.3 * internalScaling}>Clear Zone</text>
				<text
					class="explainText"
					text-anchor="middle"
					x={testStart + (backTestEnd - testStart) / 2}
					y={-height + 2.5 * internalScaling}>test</text>
				<!-- Grid size scale -->
				<text
					class="scaleText"
					text-anchor="middle"
					x={unitScale * 1.5 * internalScaling}
					y={(scaleBarPosition - 1) * unitScale * internalScaling -
						0.3 * (unitScale * internalScaling)}>{metric ? '1m' : '5ft'}</text>
				<line
					class="scaleLine"
					x1={unitScale * internalScaling}
					x2={unitScale * internalScaling}
					y1={scaleBarPosition * unitScale * internalScaling - 0.85 * (unitScale * internalScaling)}
					y2={scaleBarPosition * unitScale * internalScaling -
						1.15 * (unitScale * internalScaling)} />
				<line
					class="scaleLine"
					x1={2 * unitScale * internalScaling}
					x2={2 * unitScale * internalScaling}
					y1={scaleBarPosition * unitScale * internalScaling - 0.85 * (unitScale * internalScaling)}
					y2={scaleBarPosition * unitScale * internalScaling -
						1.15 * (unitScale * internalScaling)} />
				<line
					class="scaleLine"
					x1={unitScale * internalScaling}
					x2={2 * unitScale * internalScaling}
					y1={scaleBarPosition * unitScale * internalScaling - unitScale * internalScaling}
					y2={scaleBarPosition * unitScale * internalScaling - unitScale * internalScaling} />
			</svg>
		</div>
	{/if}
</div>

<style>
	.container {
		margin: var(--space-1) var(--space-6);
	}

	.heading {
		font-weight: var(--weight-bold);
		font-size: var(--font-size-base);
	}

	.clearZoneUnknown {
		padding: var(--space-6);
	}

	.imageContainer {
		margin: var(--space-4) auto;
		width: fit-content;
	}

	/* The plot uses the fixed palette set on the svg, so it reads the same in both themes. */
	svg {
		outline: 1px solid var(--border-strong);
		height: 20rem;

		.plotBackground {
			fill: var(--plot-background);
		}

		.gradeArea {
			stroke: var(--plot-ink);
			stroke-width: 0.3;
		}

		.gridLine {
			stroke: var(--plot-ink);
			stroke-width: 0.2;
			stroke-dasharray: 1 2;
		}

		.baseline {
			stroke: var(--plot-ink);
			stroke-width: 0.5;
		}

		.profileArea {
			opacity: 0.5;
			fill: var(--plot-profile);
		}

		.profileLine {
			fill: none;
			stroke: var(--plot-ink);
			stroke-width: 1.4;
			stroke-linejoin: round;
		}

		.czTestLine {
			opacity: 0.5;
			stroke: var(--plot-ink);
			stroke-width: 1;
		}

		.gradeText,
		.explainText,
		.scaleText {
			fill: var(--plot-ink);
			font-size: var(--font-size-xs);
		}

		.gradeText,
		.explainText {
			font-weight: var(--weight-regular);
		}

		.scaleText {
			font-weight: var(--weight-semibold);
		}

		.explainLine {
			stroke: var(--plot-ink);
			stroke-width: 1;
		}

		.explainArrow {
			fill: var(--plot-ink);
		}

		.scaleLine {
			stroke: var(--plot-ink);
			stroke-width: 1;
		}
	}
</style>
